'use client';

// ─────────────────────────────────────────────────────────────────────────────
// BottomTabBar — phone-style fixed bottom navigation for the PM dashboard.
//
// Six tabs: Today / Jobs / Schedule / Inspections / Tickets / Documents.
// Default landing tab is `today`. The bar is fixed at the viewport bottom on
// every screen size — desktop included — so the dashboard reads like a
// mobile app even on a laptop. That's intentional: a field PM might dock at
// a desk for ten minutes, then go back to a tablet on a roof.
//
// Touch targets: each tab is min-h-[64px] in total, with the icon + label
// stack centered. The full button area is tappable (no inner padding traps).
// Active state: cyan top border (2px) + cyan icon/label.
//
// Keyboard navigation: arrow keys cycle, Home / End jump to first / last.
// Tabs participate in the WAI-ARIA tablist pattern (role=tablist on the
// bar, role=tab on each button, aria-selected per active state,
// aria-controls pointing at the matching panel id in DashboardInteractive).
// ─────────────────────────────────────────────────────────────────────────────

import { useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

export type TabKey = 'today' | 'jobs' | 'schedule' | 'inspections' | 'tickets' | 'documents';

export const TAB_PANEL_ID: Record<TabKey, string> = {
  today: 'pm-tab-panel-today',
  jobs: 'pm-tab-panel-jobs',
  schedule: 'pm-tab-panel-schedule',
  inspections: 'pm-tab-panel-inspections',
  tickets: 'pm-tab-panel-tickets',
  documents: 'pm-tab-panel-documents',
};

interface TabConfig {
  key: TabKey;
  label: string;
  icon: string;
}

const TABS: TabConfig[] = [
  { key: 'today',       label: 'Today',       icon: 'HomeIcon' },
  { key: 'jobs',        label: 'Jobs',        icon: 'BriefcaseIcon' },
  { key: 'schedule',    label: 'Schedule',    icon: 'CalendarDaysIcon' },
  { key: 'inspections', label: 'Inspect',     icon: 'ClipboardDocumentCheckIcon' },
  { key: 'tickets',     label: 'Tickets',     icon: 'ExclamationTriangleIcon' },
  { key: 'documents',   label: 'Docs',        icon: 'FolderIcon' },
];

interface BottomTabBarProps {
  activeTab: TabKey;
  onTabChange: (key: TabKey) => void;
  /** Optional per-tab badge counts (e.g. open tickets). Zero/undefined hides the badge. */
  badges?: Partial<Record<TabKey, number>>;
}

export default function BottomTabBar({ activeTab, onTabChange, badges }: BottomTabBarProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = useCallback((index: number) => {
    const clamped = (index + TABS.length) % TABS.length;
    const ref = tabRefs.current[clamped];
    if (ref) {
      ref.focus();
      onTabChange(TABS[clamped].key);
    }
  }, [onTabChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTab(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTab(index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(TABS.length - 1);
    }
  }, [focusTab]);

  return (
    <nav
      role="tablist"
      aria-label="Project Manager dashboard sections"
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex"
      style={{
        zIndex: 30,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        height: '64px',
        // iOS safe-area: respect the home-indicator gutter on iPhones.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {TABS.map((tab, idx) => {
        const isActive = tab.key === activeTab;
        const badgeCount = badges?.[tab.key];
        const showBadge = typeof badgeCount === 'number' && badgeCount > 0;
        return (
          <button
            key={tab.key}
            ref={(el) => { tabRefs.current[idx] = el; }}
            role="tab"
            aria-selected={isActive}
            aria-controls={TAB_PANEL_ID[tab.key]}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.key)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-transform active:scale-95"
            style={{
              minHeight: '44px',
              color: isActive ? '#07B0E9' : '#6B7280',
              borderTop: isActive ? '2px solid #07B0E9' : '2px solid transparent',
            }}
          >
            <div className="relative">
              <Icon
                name={tab.icon}
                size={22}
                variant={isActive ? 'solid' : 'outline'}
              />
              {showBadge && (
                <span
                  className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white flex items-center justify-center"
                  style={{ fontSize: '9px', fontWeight: 700, lineHeight: 1 }}
                >
                  {badgeCount! > 99 ? '99+' : badgeCount}
                </span>
              )}
            </div>
            <span
              className="font-caption"
              style={{
                fontSize: '11px',
                fontWeight: isActive ? 600 : 500,
                lineHeight: 1,
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
